import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, Button, IconButton, Stack, Tooltip } from '@mui/material';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { DataTableSkeleton } from '@/components/common/DataTableSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadMoreButton } from '@/components/common/LoadMoreButton';
import { PageHeader } from '@/components/common/PageHeader';
import { useProfesionalesMap } from '@/features/establecimientos/hooks/useProfesionalesMap';
import { useNotify } from '@/hooks/useNotify';
import { isApiError } from '@/lib/apiError';

import { ProtocolosBulkActions } from '../components/ProtocolosBulkActions';
import { ProtocolosFiltersBar } from '../components/ProtocolosFilters';
import { ProtocolosTable } from '../components/ProtocolosTable';
import { useEstablecimientosMap } from '../hooks/useEstablecimientosMap';
import { useProtocolosFilters } from '../hooks/useProtocolosFilters';
import { useProtocolos } from '../hooks/useProtocolos';
import { useProtocolosSelection } from '../hooks/useProtocolosSelection';

export function ProtocolosPage(): JSX.Element {
  const navigate = useNavigate();
  const notify = useNotify();
  const { filters, updateFilters, clearFilters } = useProtocolosFilters();
  const selection = useProtocolosSelection();

  const {
    items,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
  } = useProtocolos({
    numero_protocolo: filters.numeroProtocolo || undefined,
    uid_profesional: filters.uidProfesional || undefined,
    uid_establecimiento: filters.uidEstablecimiento || undefined,
    facturado: filters.facturado === '' ? undefined : filters.facturado === 'true',
    cobrado: filters.cobrado === '' ? undefined : filters.cobrado === 'true',
    sort_order: filters.sortOrder,
    limit: 50,
  });

  const { getName: getProfesionalName, isLoading: profesLoading } = useProfesionalesMap();
  const { map: establecimientosMap, isLoading: estabsLoading } = useEstablecimientosMap();

  const isInitialLoading = isLoading || profesLoading || estabsLoading;

  // El BE rechaza facturado+cobrado combinados con 422; solo puede pasar por
  // manipulación manual de la URL, ya que la barra de filtros los excluye entre sí.
  useEffect(() => {
    if (isError && filters.facturado !== '' && filters.cobrado !== '') {
      notify.error('Los filtros de facturación y cobro no se pueden combinar. Se restablecieron los filtros.');
      updateFilters({ facturado: '', cobrado: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  function handleFiltersChange(partial: Parameters<typeof updateFilters>[0]): void {
    selection.clear();
    updateFilters(partial);
  }

  function handleFiltersClear(): void {
    selection.clear();
    clearFilters();
  }

  return (
    <>
      <PageHeader
        title="Protocolos"
        subtitle="Listado de protocolos del laboratorio"
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Recargar">
              <IconButton onClick={() => void refetch()} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/protocolos/nuevo')}
            >
              Nuevo protocolo
            </Button>
          </Stack>
        }
      />

      <ProtocolosFiltersBar
        filters={filters}
        onChange={handleFiltersChange}
        onClear={handleFiltersClear}
      />

      {isInitialLoading && <DataTableSkeleton columns={9} rows={5} />}

      {!isInitialLoading && isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => navigate(0)}>
              Reintentar
            </Button>
          }
        >
          {isApiError(error) ? error.message : 'No se pudieron cargar los protocolos.'}
        </Alert>
      )}

      {!isInitialLoading && !isError && items.length === 0 && (
        <EmptyState
          icon={<AssignmentIcon sx={{ fontSize: 'inherit' }} />}
          title="No hay protocolos para mostrar"
          description="Probá ajustar los filtros o crear un nuevo protocolo."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/protocolos/nuevo')}
            >
              Nuevo protocolo
            </Button>
          }
        />
      )}

      {!isInitialLoading && !isError && items.length > 0 && (
        <Box>
          <ProtocolosBulkActions
            items={items}
            selectedUids={selection.selected}
            getEstablecimientoName={(uid) => establecimientosMap.get(uid) ?? uid}
            onClearSelection={selection.clear}
          />
          <ProtocolosTable
            items={items}
            getProfesionalName={getProfesionalName}
            getEstablecimientoName={(uid) => establecimientosMap.get(uid) ?? uid}
            sortOrder={filters.sortOrder}
            onSortChange={(order) => updateFilters({ sortOrder: order })}
            selectedUids={selection.selected}
            onToggle={selection.toggle}
            onToggleAll={selection.toggleAll}
          />
          <Stack alignItems="center">
            <LoadMoreButton
              hasMore={hasNextPage}
              isLoading={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            />
          </Stack>
        </Box>
      )}
    </>
  );
}
